import React, { useEffect, useState } from "react";

import { a, config, to, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import styled from "styled-components";

import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { useOnKeyDown } from "../../../hooks/useOnKeyDown";
import type theme from "../../../styles/theme";
import ModalActionButtons from "./ModalActionButtons";
import ModalHandle from "./ModalHandle";
import ModalHeader from "./ModalHeader";
import type { ModalContainerProps, ModalOptions } from "./ModalStack.types";

const ClickEventBlocker = styled.div`
  position: absolute;
  inset: 0;
  z-index: 99998;
`;
const getMaxHeight = (
  { size, maxHeight, isMobile }: ModalOptions = {},
  padding = 0,
) => {
  const screenHeight = isMobile ? "calc(100vh - 80px)" : "100vh";
  if (maxHeight) return `${maxHeight}px`;
  if (size === "fullscreen") return `calc(${screenHeight} + ${padding}px)`;
  if (size === "full" || isMobile)
    return `calc(${screenHeight} - 20px + ${padding}px)`;
  return `calc(${screenHeight} - 80px + ${padding}px)`;
};
interface OuterProps {
  size: ModalOptions["size"];
  isMobile: ModalOptions["isMobile"];
  width: ModalOptions["width"];
  overflowing: boolean;
  paddingBottom: number;
  index: number;
  theme: typeof theme;
}
const Outer = styled(a.div)<OuterProps>`
  border-radius: ${({ theme, size }) =>
    size === "fullscreen" ? "0" : `${theme.radii[5]}px`};
  /* touch-action: none; */
  touch-action: ${({ overflowing }) => !overflowing && "none"};
  position: ${({ size, isMobile }) =>
    size === "fullscreen" && isMobile ? "fixed" : "absolute"};
  inset: ${({ size, isMobile }) =>
    size === "fullscreen" && isMobile ? "0 !important" : "initial"};
  min-height: ${({ size, isMobile }) =>
    size === "fullscreen" && isMobile && "var(--vh, 100vh)"};
  max-height: ${({ size, theme, paddingBottom }) =>
    size !== "fullscreen"
      ? `calc(var(--vh, 100vh) - ${theme.space[7]} + ${paddingBottom}px)`
      : "var(--vh, 100vh)"};
  background: ${({ theme }) => theme.colors.palette.grey};
  width: ${({ theme, size, width }) => (width ? `${width}px` : theme.width(size))};
  max-width: ${({ theme, size }) =>
    size !== "fullscreen" ? theme.width("lg", true) : null};
`;
interface InnerProps {
  size: ModalOptions["size"];
  paddingBottom: number;
  opacity: number;
  theme: typeof theme;
}
const Inner = styled(a.div)<InnerProps>`
  background-color: white;
  min-height: 120px;
  border-radius: ${({ theme, size }) =>
    size === "fullscreen" ? "0" : `${theme.radii[5]}px`};
  padding: 0;
  padding-bottom: ${({ paddingBottom }) => `${paddingBottom}px`};
  opacity: ${({ opacity }) => opacity};
  position: relative;
  z-index: 0;
`;

const ModalContainer = ({
  item,
  index,
  style,
  closeModal,
}: ModalContainerProps) => {
  const { type: ModalComponent, props } = item;
  const options = item.options || {};

  useOnKeyDown((e) => {
    if (e.code === "Escape" && options.onClose) {
      options.onClose();
    }
    // if (e.code === "Enter" && options.onSubmit) {
    //   options.onSubmit();
    // }
  });

  const height = options?.height || 0.0001;
  const realHeight = 500;

  options.onClose ??= closeModal;
  options.closeModal ??= closeModal;
  options.isMobile = useIsMobile();

  const [innerHeight, setInnerHeight] = React.useState(0);
  const [outerHeight, setOuterHeight] = React.useState(0);
  const [overflowing, setOverflowing] = React.useState(options?.swipe);
  const wrapperRef = React.useRef<HTMLElement>(null);
  const sheetRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    setTimeout(() => {
      setInnerHeight(sheetRef.current?.offsetHeight || 0);
      setOuterHeight(wrapperRef.current?.offsetHeight || 0);
      setOverflowing(outerHeight < innerHeight);
    }, 0);
  }, [innerHeight, outerHeight]);

  // if swipe
  const [{ y }, api] = useSpring(() => ({ y: height }));
  const [dragged, setDragged] = useState(0);
  const swipePadding = 1000;
  const open = ({ canceled }) => {
    api.start({
      y: 0,
      immediate: false,
      config: canceled ? config.wobbly : config.stiff,
    });
  };
  const close = (velocity = 0) => {
    // onDrag(0.001)
    api.start({
      y: height + swipePadding,
      immediate: false,
      config: { ...config.stiff, velocity },
      onRest: () => closeModal(),
    });
  };

  const bind = useDrag(
    ({
      last,
      velocity: [, vy],
      direction: [, dy],
      movement: [, my],
      cancel,
      canceled,
    }) => {
      setDragged(my);

      if (my < -70) cancel();
      if (!last) return api.start({ y: my, immediate: true });
      if (my > realHeight * 0.2 || (vy > 0.5 && dy > 0)) return close(vy);

      return open({ canceled });
    },
    {
      from: () => [0, y.get()],
      filterTaps: true,
      bounds: { top: 0 },
      rubberband: true,
    },
  );

  const bindContainer = options?.isMobile &&
    !overflowing &&
    options?.size !== "fullscreen" && { ...bind() };
  const bindHandle = options?.isMobile && overflowing && { ...bind() };

  const PADDING = options?.isMobile ? 100 : 0;
  const outerStyle = {
    ...style,
    y: to([y, style?.y || 0], (dragged, offset) => dragged + offset),
    // maxWidth: theme.width("lg", true),
    // maxHeight: getMaxHeight(options, PADDING),
    overflow: options?.overflow
      ? options?.overflow
      : overflowing
        ? "auto"
        : "hidden",
    bottom: options?.isMobile ? -PADDING : "auto",
  };
  const innerStyle = {
    paddingBottom:
      options?.size !== "fullscreen" && options?.isMobile ? PADDING : 0,
    opacity: style?.shading,
  };
  // console.log(ModalComponent, typeof ModalComponent)
  // if (typeof ModalComponent !== "string") return null

  let timeout = undefined as ReturnType<typeof setTimeout> | undefined;
  clearTimeout(timeout);
  const pause = React.useRef(false);
  const handleClose =
    typeof closeModal === "function"
      ? () => {
          if (!pause.current) {
            closeModal();
            pause.current = true;
          }
          timeout = setTimeout(() => {
            pause.current = false;
          }, 3000);
        }
      : false;

  return (
    <Outer
      {...bindContainer}
      size={options?.size}
      isMobile={options?.isMobile}
      width={options?.width}
      overflowing={overflowing}
      paddingBottom={PADDING}
      style={outerStyle}
      ref={wrapperRef}
      index={index}
    >
      <Inner
        paddingBottom={PADDING}
        size={options?.size}
        style={innerStyle}
        ref={sheetRef}
        data-cy={options?.["data-cy"]}
        id={options?.id}
      >
        <div
          {...bindHandle}
          style={{ position: "sticky", top: 0, zIndex: 999 }}
        >
          <ModalHandle {...options} style={{ backgroundColor: undefined }} />
        </div>
        <ModalHeader {...options} />
        {options?.style ? (
          <div style={{ ...options?.style }}>
            <ModalComponent {...props} />
          </div>
        ) : (
          <ModalComponent {...props} />
        )}
        <ModalActionButtons {...options} />
        {index > 0 && <ClickEventBlocker onClick={handleClose} />}
      </Inner>
    </Outer>
  );
};

export default ModalContainer;
